import React, { useCallback, useEffect, useState } from "react";
import MicIcon from "@mui/icons-material/Mic";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Button,
  Divider,
  Chip,
} from "@mui/material";
import { Eye, Pencil, Trash2, X } from "lucide-react";
import SearchInput from "@/components/SearchBar";
import { useDashboardTheme } from "@/pages/MeetingDashboard/themeHelper";
import {
  useDeleteScriptMeta,
  useScriptCardData,
  useScriptDetails,
  useReassignScriptAssignment,
} from "@/hooks/useTenantDashboardService";
import useAuthStore from "@/store/useAuthStore";
import { formatDateUS } from "@/utils/dateUtils";
import { toast } from "react-toastify";
import type { ScriptDetails } from "@/types/script";
import type { ScriptCardDto } from "@/types/tenantService";
import type { AxiosError } from "axios";
import Pagination from "@/components/Pagination";

type Props = {
  setShowAddScriptUI: (val: boolean) => void;
  onEdit: (script: ScriptDetails, value: boolean) => void;
};

const ScriptListView: React.FC<Props> = ({ setShowAddScriptUI, onEdit }) => {
  const { isDark } = useDashboardTheme();
  const { tenantid } = useAuthStore();
  const tenantId = localStorage.getItem("tenantId") ?? tenantid;
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const tableCellStyle = {
    color: "var(--color-text)",
    fontWeight: "bold",
    textTransform: "uppercase",
    fontSize: "0.80rem",
    borderBottom: "1px solid var(--color-border)",
  };
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] =
    useState<string>(searchTerm);
  const {
    data: scriptResponse,
    isLoading: loadingScripts,
    error,
    refetch,
  } = useScriptCardData("", tenantId, page, limit, debouncedSearchTerm);
  const { mutate: deleteScript } = useDeleteScriptMeta();
  const { mutate: reassignScript, isPending: isReassigning } = useReassignScriptAssignment();
  const scripts = scriptResponse?.data?.data ?? [];
  const totalPages = Math.ceil((scriptResponse?.data?.total ?? 0) / limit);
  const [selectedMetaId, setSelectedMetaId] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedDeleteId, setSelectedDeleteId] = useState<string | null>(null);

  const handleDelete = (scriptMetaId: string) => {
    deleteScript(scriptMetaId, {
      onSuccess: () => {
        toast.success("Script deleted successfully");
        refetch();
      },
      onError: (err: Error) => {
        let message = "Failed to delete script";
        if ((err as AxiosError).isAxiosError) {
          const axiosErr = err as AxiosError<{ message?: string }>;
          message = axiosErr.response?.data?.message || message;
        }
        toast.error(message);
      },
    });
  };

  const handleDeleteClick = (scriptMetaId: string) => {
    setSelectedDeleteId(scriptMetaId);
    setShowDeleteDialog(true);
  };

  const handleConfirmDelete = () => {
    if (selectedDeleteId) {
      handleDelete(selectedDeleteId);
    }
    setShowDeleteDialog(false);
    setSelectedDeleteId(null);
  };

  const handleCancelDelete = () => {
    setShowDeleteDialog(false);
    setSelectedDeleteId(null);
  };

  const handleReassignClick = (script: ScriptCardDto) => {
    if (!script.assignmentId || !script.assignedTo?.id) {
      toast.error("Cannot reassign: No user currently assigned to this script.");
      return;
    }

    reassignScript(
      {
        assignmentId: script.assignmentId,
        assignedToId: script.assignedTo.id,
      },
      {
        onSuccess: () => {
          toast.success("Script successfully reassigned and counts updated!");
          refetch();
        },
        onError: (err: any) => {
          let message = "Failed to reassign script";
          if (err?.response?.data?.message) {
            message = err.response.data.message;
          }
          toast.error(message);
        },
      }
    );
  };

  const handleViewClick = (metaId: string, isDialog: boolean) => {
    setSelectedMetaId(metaId);
    setDialogOpen(isDialog);
  };

  const handleSearch = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value),
    [],
  );

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedMetaId(null);
  };

  const { data: scriptDetailsData, isLoading: loadingDetails } =
    useScriptDetails(selectedMetaId ?? "");

  const [expandedSections, setExpandedSections] = useState<boolean[]>([]);

  useEffect(() => {
    if (scriptDetailsData?.sections) {
      setExpandedSections(
        new Array(scriptDetailsData.sections.length).fill(false),
      );

      onEdit(
        {
          ...scriptDetailsData,
          scriptMeta: {
            ...scriptDetailsData.scriptMeta,
            overallscore:
              scriptDetailsData.scriptMeta.overallscore?.toString() ?? null,
          },
        },
        !dialogOpen,
      );
    }
  }, [scriptDetailsData]);

  const toggleSection = (index: number) => {
    setExpandedSections((prev) =>
      prev.map((val, i) => (i === index ? !val : val)),
    );
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 1000);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  return (
    <Box
      className={localStorage.getItem("theme") === "light" ? "light-theme-dashboard" : ""}
      sx={{
        mt: 8,
        p: 4,
        minHeight: "100vh",
        backgroundColor: "var(--color-bg)",
        color: "var(--color-text)",
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="h5">
          <SearchInput
            placeholder="Search here..."
            value={searchTerm}
            onChange={handleSearch}
          />
        </Typography>
        <Button
          onClick={() => setShowAddScriptUI(true)}
          sx={{
            backgroundColor: "#C1FF72",
            color: "#000",
            fontWeight: "bold",
            borderRadius: 999,
            px: 3,
            py: 1,
            textTransform: "none",
            "&:hover": {
              backgroundColor: "#D7FF9B",
            },
          }}
        >
          Add Script
        </Button>
      </Box>

      {loadingScripts ? (
        <Box display="flex" justifyContent="center" mt={6}>
          <CircularProgress color="inherit" />
        </Box>
      ) : error ? (
        <Typography color="error" mt={4}>
          Failed to load scripts.
        </Typography>
      ) : scripts?.length === 0 ? (
        <Box textAlign="center" mt={8}>
          <Typography>No scripts found.</Typography>
        </Box>
      ) : (
        <TableContainer
          component={Paper}
          sx={{
            backgroundColor: "var(--color-surface)",
            border: "1px solid var(--color-border)",
            mt: 4,
            borderRadius: 2,
            overflowX: "auto",
          }}
        >
          <Table>
            <TableHead>
              <TableRow
                sx={{
                  backgroundColor: "var(--color-table-header)",
                }}
              >
                <TableCell sx={tableCellStyle}>Title</TableCell>
                <TableCell sx={tableCellStyle}>Target Company</TableCell>
                <TableCell sx={tableCellStyle}>Assigned To</TableCell>
                <TableCell sx={tableCellStyle}>Reassigned</TableCell>
                <TableCell sx={tableCellStyle}>Attempts</TableCell>
                <TableCell sx={tableCellStyle}>Type</TableCell>
                <TableCell sx={tableCellStyle}>Created At</TableCell>
                <TableCell sx={tableCellStyle}>Reassign</TableCell>
                <TableCell sx={tableCellStyle}>View</TableCell>
                <TableCell sx={tableCellStyle}>Edit</TableCell>
                <TableCell sx={tableCellStyle}>Delete</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {scripts.map((script) => (
                <TableRow
                  key={script.scriptMetaId}
                  hover
                  sx={{
                    "&:hover": {
                      backgroundColor: "color-mix(in srgb, var(--color-stat-accent) 5%, transparent) !important",
                    },
                  }}
                >
                  <TableCell sx={{ color: "var(--color-text)" }}>{script.title}</TableCell>
                  <TableCell sx={{ color: "var(--color-text)" }}>
                    {script.targetCompanyProfile?.companyName || "N/A"}
                  </TableCell>
                  <TableCell sx={{ color: "var(--color-text)" }}>
                    {script.assignedTo?.name ? script.assignedTo.name : "Unassigned"}
                  </TableCell>
                  <TableCell sx={{ color: "var(--color-text)" }}>
                    {script.reassignCount || 0}
                  </TableCell>
                  <TableCell sx={{ color: "var(--color-text)" }}>
                    {script.attemptCount || 0}
                  </TableCell>
                  <TableCell
                    sx={{ color: "var(--color-text)", textTransform: "capitalize" }}
                  >
                    {script.scripttype}
                  </TableCell>
                  <TableCell sx={{ color: "var(--color-text)" }}>
                    {formatDateUS(script.assignedAt)}
                  </TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => handleReassignClick(script)}
                      disabled={isReassigning}
                      sx={{
                        color: isDark ? "#38bdf8" : "#0284c7",
                        borderColor: isDark ? "#38bdf8" : "#0284c7",
                        fontSize: "0.75rem",
                        textTransform: "none",
                        py: 0.25,
                      }}
                    >
                      {isReassigning ? "..." : "Reassign"}
                    </Button>
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => handleViewClick(script.scriptMetaId, true)}
                      sx={{ color: isDark ? "#B8F890" : "#5a9d50" }}
                      title="View Script"
                    >
                      <Eye size={18} />
                    </IconButton>
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => handleViewClick(script.scriptMetaId, false)}
                      sx={{ color: isDark ? "#38bdf8" : "#0284c7" }}
                      title="Edit Script"
                    >
                      <Pencil size={18} />
                    </IconButton>
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDeleteClick(script.scriptMetaId)}
                    >
                      <Trash2 size={16} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {!dialogOpen && totalPages > 1 && (
        <Box mt={4} display="flex" justifyContent="center">
          <Pagination
            totalPages={totalPages}
            currentPage={page}
            setCurrentPage={setPage}
          />
        </Box>
      )}

      {/* Dialog for script details */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        fullWidth
        maxWidth="md"
        PaperProps={{
          className: localStorage.getItem("theme") === "light" ? "light-theme-dashboard" : "",
          sx: { backgroundColor: "var(--color-surface)", color: "var(--color-text)", border: "1px solid var(--color-border)" }
        }}
      >
        <DialogTitle
          sx={{
            borderBottom: "1px solid var(--color-border)",
            pb: 2,
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="flex-start"
            flexWrap="wrap"
          >
            <Box>
              <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                <Box component="span" sx={{ color: "var(--color-stat-accent)" }}>
                  Script Details
                </Box>{" "}
                :{" "}
                <Box component="span" sx={{ color: "var(--color-text)" }}>
                  {scriptDetailsData?.scriptMeta?.title || ""}
                </Box>
              </Typography>

              {scriptDetailsData && (
                <Box
                  mt={1}
                  display="flex"
                  flexWrap="wrap"
                  alignItems="center"
                  gap={1}
                >
                  <Box
                    sx={{
                      backgroundColor: "#B8F890",
                      color: "#000",
                      fontWeight: "bold",
                      fontSize: "0.65rem",
                      borderRadius: "12px",
                      px: 1.25,
                      py: 0.5,
                      textTransform: "uppercase",
                    }}
                  >
                    {scriptDetailsData.scriptMeta.scripttype}
                  </Box>

                  <Typography
                    variant="body2"
                    sx={{ color: "var(--color-muted)", ml: 1, fontSize: "0.85rem" }}
                  >
                    Created At:{" "}
                    {formatDateUS(scriptDetailsData.scriptMeta.createdAt)}
                  </Typography>
                </Box>
              )}
            </Box>

            <IconButton onClick={handleCloseDialog} sx={{ color: "var(--color-text)" }}>
              <X />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          {loadingDetails ? (
            <Box display="flex" justifyContent="center" p={2}>
              <CircularProgress color="inherit" />
            </Box>
          ) : scriptDetailsData ? (
            <>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: "bold", color: "var(--color-stat-accent)" }}
              >
                ASSIGNED USERS
              </Typography>

              {scriptDetailsData.scriptMeta.ScriptGenAssignment.length === 0 ? (
                <Typography>No assignments found.</Typography>
              ) : (
                scriptDetailsData.scriptMeta.ScriptGenAssignment.map(
                  (assignment, index) => (
                    <Box
                      key={assignment.id ?? index}
                      mb={3}
                      p={2}
                      sx={{ border: "1px solid var(--color-border)", borderRadius: 2 }}
                    >
                      <Box display={"flex"}>
                        <Typography
                          variant="subtitle2"
                          sx={{ color: "var(--color-stat-accent)" }}
                        >
                          Assigned To:
                        </Typography>
                        <Typography sx={{ ml: 1 }}>
                          {assignment.user?.name} ({assignment.user?.email})
                        </Typography>
                      </Box>
                      <Box display={"flex"}>
                        <Typography
                          variant="subtitle2"
                          sx={{ color: "var(--color-stat-accent)" }}
                        >
                          Assigned By:
                        </Typography>
                        <Typography sx={{ ml: 1 }}>
                          {
                            assignment
                              .User_ScriptGenAssignment_assignedByIdToUser?.name
                          }
                        </Typography>
                      </Box>

                      {assignment.completedAt && (
                        <>
                          <Typography
                            variant="subtitle2"
                            sx={{ mt: 1, color: "var(--color-stat-accent)" }}
                          >
                            Completed At:
                          </Typography>
                          <Typography sx={{ ml: 1 }}>
                            {formatDateUS(assignment.completedAt)}
                          </Typography>
                        </>
                      )}
                    </Box>
                  ),
                )
              )}

              <Divider sx={{ mb: 2, borderColor: "var(--color-border)" }} />
              {scriptDetailsData.sections.map((section, index) => {
                const expanded = expandedSections[index] || false;
                const needsToggle = section.content.length > 300;

                return (
                  <Box key={index} mt={3}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: "bold", color: "var(--color-stat-accent)" }}
                      >
                        {/* {section.type.replace(/_/g, " ")} */}
                        {index + 1}.{" "}
                        {section.type === "UNDERSTAND_OBJECTION" ||
                        section.type === "understand_objection"
                          ? "UNDERSTAND PURPOSE"
                          : section.type.replace(/_/g, " ")}
                      </Typography>

                      {section.isPracticeSection && (
                        <Chip
                          icon={<MicIcon sx={{ color: "black" }} />}
                          label="Practice"
                          size="small"
                          sx={{
                            backgroundColor: "var(--color-stat-accent)",
                            color: "black",
                            fontWeight: "bold",
                            height: "24px",
                          }}
                        />
                      )}
                    </Box>

                    <Typography
                      sx={{
                        color: "var(--color-text)",
                        whiteSpace: "pre-line",
                        mt: 0.5,
                        opacity: 0.9,
                        overflow: "hidden",
                        display: "-webkit-box",
                        WebkitBoxOrient: "vertical",
                        WebkitLineClamp: expanded ? "none" : 4,
                      }}
                    >
                      {section.content}
                    </Typography>

                    {needsToggle && (
                      <Button
                        onClick={() => toggleSection(index)}
                        sx={{
                          color: "#B8F890",
                          textTransform: "none",
                          fontSize: "0.75rem",
                          mt: 0,
                          px: 0,
                        }}
                      >
                        {expanded ? "Show Less" : "Read More"}
                      </Button>
                    )}
                  </Box>
                );
              })}
            </>
          ) : (
            <Typography color="error">
              Failed to load script details.
            </Typography>
          )}
        </DialogContent>
      </Dialog>

      {/* NEW DELETE CONFIRMATION DIALOG */}
      <Dialog
        open={showDeleteDialog}
        onClose={handleCancelDelete}
        PaperProps={{
          className: localStorage.getItem("theme") === "light" ? "light-theme-dashboard" : "",
          sx: { backgroundColor: "var(--color-surface)", color: "var(--color-text)", border: "1px solid var(--color-border)" }
        }}
      >
        <DialogTitle sx={{ fontWeight: "bold", color: "var(--color-stat-accent)" }}>
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Are you sure you want to delete this script?
          </Typography>
          <Box display="flex" justifyContent="flex-end" gap={2}>
            <Button
              variant="outlined"
              onClick={handleCancelDelete}
              sx={{
                color: "var(--color-stat-accent)",
                borderColor: "var(--color-stat-accent)",
                "&:hover": { backgroundColor: "var(--color-divider)" },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirmDelete}
              sx={{
                backgroundColor: "var(--color-stat-accent)",
                color: "black",
                fontWeight: "bold",
                "&:hover": { opacity: 0.9 },
              }}
            >
              Delete
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ScriptListView;
